<template>
  <v-dialog :value="value" max-width="560" persistent @input="$emit('input', $event)">
    <v-card :loading="loading">
      <v-card-title>
        <v-icon left color="teal darken-1">mdi-file-document-outline</v-icon>
        {{ title }}
      </v-card-title>
      <v-card-text>
        <p v-if="hint" class="body-2 mb-4">{{ hint }}</p>

        <v-alert
          v-if="linkedInvoice"
          type="info"
          dense
          outlined
          class="mb-4"
        >
          {{
            $t("STOCK.INVOICES.ALREADY_ON_DRAFT_INVOICE", {
              code: linkedInvoice.code,
            })
          }}
        </v-alert>

        <template v-else>
          <v-radio-group v-model="mode" class="mt-0" hide-details>
            <v-radio
              :label="$t('STOCK.INVOICES.LINK_MODE_NEW')"
              value="new"
            ></v-radio>
            <v-radio
              :label="$t('STOCK.INVOICES.LINK_MODE_EXISTING')"
              value="existing"
              :disabled="!editableInvoices.length"
            ></v-radio>
          </v-radio-group>

          <v-select
            v-if="mode === 'existing'"
            v-model="selectedInvoiceId"
            :items="editableInvoices"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.INVOICES.SELECT_DRAFT_INVOICE')"
            class="mt-4"
            hide-details
          ></v-select>

          <p
            v-if="mode === 'existing' && !editableInvoices.length"
            class="caption grey--text mt-2 mb-0"
          >
            {{ $t("STOCK.INVOICES.NO_DRAFT_INVOICES") }}
          </p>

          <v-text-field
            v-if="needsSupplierNumber"
            v-model="externalNumber"
            class="mt-4"
            :label="$t('STOCK.INVOICES.SUPPLIER_INVOICE_NUMBER') + '*'"
            :error-messages="externalNumberError ? [externalNumberError] : []"
            hide-details="auto"
            outlined
            dense
            @input="externalNumberError = ''"
          ></v-text-field>
          <p v-if="needsSupplierNumber" class="caption grey--text mt-2 mb-0">
            {{ $t("STOCK.INVOICES.SUPPLIER_INVOICE_NUMBER_HINT") }}
          </p>

          <v-switch
            v-if="showPassThrough && !linkedInvoice"
            v-model="passThroughExtraCosts"
            :label="$t('STOCK.INVOICES.PASS_THROUGH_EXTRA_COSTS')"
            color="teal darken-1"
            hide-details
            class="mt-4"
          ></v-switch>
          <p
            v-if="showPassThrough && !linkedInvoice"
            class="caption grey--text mt-2 mb-0"
          >
            {{ $t("STOCK.INVOICES.PASS_THROUGH_HINT") }}
          </p>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="submitting" @click="close">
          {{ $t("GENERAL.LABELS.CANCEL") }}
        </v-btn>
        <v-btn
          color="success"
          :loading="submitting"
          :disabled="confirmDisabled"
          @click="confirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  GET_STOCK_INVOICE_ACTION_LINK_OPTIONS,
  GET_STOCK_INVOICE_DELIVERY_NOTE_LINK_OPTIONS,
} from "@/store/stock/stockInvoices.module";

export default {
  name: "StockInvoiceLinkDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    actionId: {
      type: [Number, String],
      default: null,
    },
    actionIds: {
      type: Array,
      default: () => [],
    },
    deliveryNoteIds: {
      type: Array,
      default: () => [],
    },
    kind: {
      type: String,
      default: "receipt",
      validator: (value) => ["receipt", "delivery"].includes(value),
    },
    title: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: "",
    },
    showPassThrough: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      mode: "new",
      selectedInvoiceId: null,
      passThroughExtraCosts: false,
      direction: null,
      externalNumber: "",
      externalNumberError: "",
      linkedInvoice: null,
      editableInvoices: [],
    };
  },
  computed: {
    confirmLabel() {
      if (this.linkedInvoice) {
        return this.$t("STOCK.INVOICES.OPEN_DRAFT_INVOICE");
      }
      if (this.mode === "existing") {
        return this.$t("STOCK.INVOICES.ADD_TO_SELECTED_INVOICE");
      }
      return this.$t("GENERAL.LABELS.CREATE_BUTTON");
    },
    needsSupplierNumber() {
      return this.direction === "incoming" && this.mode === "new" && !this.linkedInvoice;
    },
    confirmDisabled() {
      if (this.loading || this.submitting) {
        return true;
      }
      if (this.linkedInvoice) {
        return false;
      }
      if (this.mode === "existing") {
        return !this.selectedInvoiceId;
      }
      if (this.needsSupplierNumber && !(this.externalNumber || "").trim()) {
        return true;
      }
      return false;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        this.loadOptions();
      }
    },
  },
  methods: {
    reset() {
      this.mode = "new";
      this.selectedInvoiceId = null;
      this.passThroughExtraCosts = false;
      this.direction = null;
      this.externalNumber = "";
      this.externalNumberError = "";
      this.linkedInvoice = null;
      this.editableInvoices = [];
    },
    close() {
      this.$emit("input", false);
    },
    async loadOptions() {
      const noteIds = (this.deliveryNoteIds || []).map(Number).filter(Boolean);
      const primaryActionId = this.actionId || (this.actionIds[0] ? Number(this.actionIds[0]) : null);
      if (!noteIds.length && !primaryActionId) {
        return;
      }

      this.loading = true;
      try {
        const data = noteIds.length
          ? await this.$store.dispatch(GET_STOCK_INVOICE_DELIVERY_NOTE_LINK_OPTIONS, {
              delivery_note_ids: noteIds,
            })
          : await this.$store.dispatch(GET_STOCK_INVOICE_ACTION_LINK_OPTIONS, primaryActionId);
        this.direction = data.direction || null;
        this.linkedInvoice = data.linked_editable_invoice || null;
        this.editableInvoices = (data.editable_invoices || []).map((invoice) => ({
          ...invoice,
          label: [invoice.code, invoice.external_number, invoice.company_name]
            .filter(Boolean)
            .join(" · "),
        }));
        if (this.editableInvoices.length === 1) {
          this.selectedInvoiceId = this.editableInvoices[0].id;
        }
      } catch (errors) {
        this.$emit("error", errors);
        this.close();
      } finally {
        this.loading = false;
      }
    },
    confirm() {
      if (this.linkedInvoice) {
        this.$emit("confirm", {
          stock_invoice_id: this.linkedInvoice.id,
          openOnly: true,
        });
        return;
      }

      const payload = {
        pass_through_extra_costs: this.passThroughExtraCosts,
      };

      if (this.needsSupplierNumber) {
        payload.external_number = (this.externalNumber || "").trim();
      }

      if (this.mode === "existing") {
        payload.stock_invoice_id = this.selectedInvoiceId;
      }

      this.$emit("confirm", payload);
    },
    applySubmitError(errors) {
      const messages = errors && errors.errors && errors.errors.external_number;
      const message = Array.isArray(messages) ? messages[0] : "";
      if (!message) {
        return false;
      }
      this.externalNumberError = message;
      return true;
    },
  },
};
</script>
